import { TutorialSection } from './tutorial-step.model';

const MEDIA_BASE = 'assets/tutorial/forms-builder';

export const TUTORIAL_SECTIONS: TutorialSection[] = [
  {
    id: 'finder',
    title: 'Form Finder',
    icon: 'search',
    steps: [
      {
        id: 'form-finder-overview',
        title: 'Form Finder Overview',
        section: 'finder',
        description:
          'The Form Finder is your starting point for managing forms. It features a two-panel layout: the left sidebar contains a search bar and a scrollable list of all forms, while the right panel displays the details, properties, and versions of the selected form.',
        mediaUrl: `${MEDIA_BASE}/01-form-finder-overview.webm`,
        duration: 7,
        tips: [
          'Click any form in the left list to view its details on the right.',
          'The form list is sorted alphabetically for quick scanning.',
        ],
      },
      {
        id: 'searching-forms',
        title: 'Searching for Forms',
        section: 'finder',
        description:
          'Use the search bar at the top of the left panel to quickly find forms by name. Results appear in real time as you type, sorted by how closely they match your search term. Click the X button to clear the search and return to the full form list.',
        mediaUrl: `${MEDIA_BASE}/02-searching-forms.webm`,
        duration: 7,
        tips: [
          'Search matches form names — try typing the first few letters.',
          'Results are ranked by how closely the match appears at the start of the name.',
        ],
      },
      {
        id: 'creating-new-form',
        title: 'Creating a New Form',
        section: 'finder',
        description:
          'Click the "New Form" button in the top-right toolbar to open the creation dialog. You can either create a brand new empty form or duplicate an existing form as a starting point. Enter a name, select a category and data source, then confirm to add the form to your list.',
        mediaUrl: `${MEDIA_BASE}/03-creating-new-form.webm`,
        duration: 14,
        tips: [
          'Duplicating an existing form copies all its versions and paragraphs.',
          'You can always change the form name and properties later.',
        ],
      },
      {
        id: 'form-properties',
        title: 'Form Properties',
        section: 'finder',
        description:
          'After selecting a form, expand the "Properties" panel on the right to view and edit its settings. You can change the form name, category, and data source, and toggle "Flag as needing signature", "Require tooth selection", and "Show \'Complete Form\' button". Click "Save Properties" to persist your changes.',
        mediaUrl: `${MEDIA_BASE}/04-form-properties.webm`,
        duration: 11,
        tips: [
          '"Custom Data Source" allows you to link the form to a specific data source for dynamic content.',
          '"Flag as needing signature" is a label only — it shows "Need Signature" on the patient\'s form details. It does not add a signature field. To actually require one, add a Signature item to the form and mark that item Required.',
          '"Require tooth selection" is specific to dental forms. It does not add a field — staff are prompted with the tooth picker when adding or opening the form on a case, and cannot add the form without selecting a tooth.',
          '"Show \'Complete Form\' button" adds a submit button at the bottom of the form. Without it, the form has no explicit submit step and entries are just saved as staff fill it in.',
        ],
      },
      {
        id: 'managing-versions',
        title: 'Managing Versions',
        section: 'finder',
        description:
          'Each form can have multiple versions. The versions table shows each version\'s number, status (Active, Draft, Inactive, etc.), last edited date, and paragraph count. Use the action buttons on each row to Edit, View (read-only), Duplicate, or Delete a version. Click "New Version" to create a blank version.',
        mediaUrl: `${MEDIA_BASE}/05-managing-versions.webm`,
        duration: 7,
        tips: [
          'Only one version should be "Active" at a time — this is the version patients see.',
          'Use "Duplicate" to create a copy of an existing version for revisions.',
          'Deleted versions are soft-deleted and can potentially be recovered.',
        ],
      },
      {
        id: 'entering-editor',
        title: 'Entering the Editor',
        section: 'finder',
        description:
          'Click the Edit (pencil) icon on any version row to open the Paragraph Builder editor. You can also click the Eye icon to open in read-only view mode. The editor will load with all paragraphs and items for that version.',
        mediaUrl: `${MEDIA_BASE}/06-entering-editor.webm`,
        duration: 5,
        tips: [
          'Edit mode allows full modification. View mode is read-only.',
          'The editor opens in a separate route — use the back arrow to return to the Form Finder.',
        ],
      },
    ],
  },
  {
    id: 'editor',
    title: 'Form Editor',
    icon: 'edit',
    steps: [
      {
        id: 'editor-overview',
        title: 'Editor Overview',
        section: 'editor',
        description:
          'The Form Editor (Paragraph Builder) has a three-panel layout. The left sidebar shows your paragraphs list and paragraph properties. The center area is the builder canvas where you arrange items into rows. The right sidebar is the items library with search, Quick Add templates, and categorized form controls.',
        mediaUrl: `${MEDIA_BASE}/07-editor-overview.webm`,
        duration: 14,
        tips: [
          'The breadcrumb at the top shows: Form Name > Version > Current Paragraph.',
          'Use the back arrow to return to the Form Finder at any time.',
        ],
      },
      {
        id: 'creating-paragraphs',
        title: 'Creating & Configuring Paragraphs',
        section: 'editor',
        description:
          'Paragraphs are the building blocks of a form — each paragraph contains rows of items. Click the "+" button in the left sidebar header to create a new paragraph. In the Properties tab, give it a descriptive name, set the orientation (portrait/landscape) and item layout.',
        mediaUrl: `${MEDIA_BASE}/08-creating-paragraphs.webm`,
        duration: 18,
        tips: [
          'Each paragraph appears as a separate section on the rendered form.',
          'Click a paragraph name in the list to select it and load it in the builder canvas.',
          'Item layout controls whether items stack vertically or flow in a grid.',
        ],
        keyboardShortcut: 'Ctrl+N — New paragraph',
      },
      {
        id: 'paragraph-manipulation',
        title: 'Paragraph Manipulation',
        section: 'editor',
        description: 'Toggle options like "Page Break After" (forces a new page in PDF) and "Repeating Paragraph" (allows the paragraph to be duplicated by the user filling out the form). You can reorder paragraphs by dragging them in the list. Click the duplicate icon to copy a paragraph, which you can then edit separately. Use the delete icon to remove a paragraph — this action is irreversible.',
        mediaUrl: `${MEDIA_BASE}/09-paragraph-manipulation.webm`,
        duration: 15,
        tips: [
          '"Page Break After" is useful for long forms to control pagination.',
          '"Repeating Paragraph" is ideal for sections like "Medications" where users may need to add multiple entries.',
          'Reorder paragraphs by dragging them up or down in the left sidebar list.',
          'Duplicating a paragraph copies all its items and settings — a quick way to create similar sections.',
          'Be cautious with deleting paragraphs — this cannot be undone.',
        ]
      },
      {
        id: 'dragging-items',
        title: 'Dragging Items into Rows',
        section: 'editor',
        description:
          'The right sidebar contains all available form controls (TextBox, Checkbox, Date, Signature, etc.). Drag any item from the library and drop it into a row in the builder canvas. Items snap into place and can be reordered within the row or moved between rows.',
        mediaUrl: `${MEDIA_BASE}/10-dragging-items.webm`,
        duration: 19,
        tips: [
          'A blue highlight shows where the item will land when you drop it.',
          'You can drag items between rows to rearrange your layout.',
          'Items in the same row appear side by side on the rendered form.',
        ],
      },
      {
        id: 'changing-text',
        title: 'Changing Text',
        section: 'editor',
        description:
          'To edit an item\'s label or text, click the item card to select it and then click the text directly to edit it inline. You can change the display label, post-label, and default value this way. Press Enter or click outside the field to confirm. This allows quick edits without opening the full Item Configuration dialog.',
        mediaUrl: `${MEDIA_BASE}/11-changing-text.webm`,
        duration: 28,
        tips: [
          'Click directly on the label text to edit it inline.',
          'For more detailed text options, open the full Item Configuration dialog via the settings icon.',
        ],
      },
      {
        id: 'item-configuration',
        title: 'Item Configuration',
        section: 'editor',
        description:
          'Click the settings icon on any item card to open the Item Configuration dialog. Basics (name, control type, label, post-label, and behavior toggles) are always visible at the top. Below that, expandable sections cover Values & Defaults (valid values and default selections), Layout (column width, row height, label alignment), Hierarchy (parent/child item relationships), and Advanced (before/after actions and Note Builder settings) &mdash; expand only the ones you need.',
        mediaUrl: `${MEDIA_BASE}/12-item-configuration.webm`,
        duration: 10,
        tips: [
          'The control type determines what kind of input the user sees (text box, dropdown, date picker, etc.).',
          'Use "Valid Values" to define dropdown or radio button options.',
          '"Is Required" makes the field mandatory for form completion.',
          'The Note Builder section lets you include the item\'s value in auto-generated clinical notes.',
        ],
       },
       {
        id: 'item-size-configuration',
        title: 'Item Size Configuration',
        section: 'editor',
        mediaUrl: `${MEDIA_BASE}/12.5-item-size-configuration.webm`,
        duration: 20,
        description:
          'In the Item Configuration dialog\'s Layout section, Column Width and Row Height control the size of the item as a percentage of the screen. A column width of 20 means the item takes up 20% of the available row width. If left blank, both use a default size.',
        tips: [
          'Column widths in the same row should add up to 100% (or less) so items fit without wrapping.',
          'Smaller values create more compact, side-by-side layouts; larger values give a field more room.',
        ],
       },
       {
        id: 'row-management',
        title: 'Row Management',
        section: 'editor',
        description:
          'Rows organize items horizontally within a paragraph. Use the "Insert Row" buttons (appearing above, between, and below existing rows) to add new rows. Drag the row handle to reorder entire rows. Empty rows show a delete button for cleanup. Items dropped at the bottom auto-create a new row if needed.',
        mediaUrl: `${MEDIA_BASE}/13-row-management.webm`,
        duration: 30,
        tips: [
          'Each row becomes a horizontal line of fields in the rendered form.',
          'You can have as many items in a row as needed — they share the available width.',
          'Delete empty rows to keep your form layout clean.',
        ],
      },
      {
        id: 'repeatable-paragraphs',
        title: 'Repeatable Paragraphs',
        section: 'editor',
        description:
          'To allow users filling out the form to add multiple entries for a section (e.g., multiple medications), toggle the "Repeating Paragraph" option in the paragraph properties. This adds an "Add Another" button at the end of the paragraph in the rendered form, letting users create as many copies of that paragraph as needed.',
        mediaUrl: `${MEDIA_BASE}/13.5-repeatable-paragraphs.webm`,
        duration: 30,
        tips: [
          'Repeatable paragraphs are ideal for sections where the number of entries is unknown (e.g., surgeries, allergies).',
          'Users filling out the form can click "Add Another" to create a new instance of the paragraph with the same fields.',
          'Each instance is treated separately in form results, allowing for multiple entries to be captured cleanly.',
        ]
      },
      {
        id: 'required-and-duplicating',
        title: 'Marking Required & Duplicating',
        section: 'editor',
        description:
          'Use the quick action buttons on each item card to toggle required status and duplicate items. Click the asterisk (*) icon to mark an item as required — required items display a red asterisk on the rendered form. Click the duplicate icon to create a copy of the item in the same row, which you can then rename and reconfigure. These quick actions save time compared to opening the full configuration dialog.',
        mediaUrl: `${MEDIA_BASE}/14-required-and-duplicating.webm`,
        duration: 27,
        tips: [
          'Required items show a red asterisk (*) next to their label on the form.',
          'Duplicating an item copies all its settings — just update the name and label.',
          'Both actions are available as hover buttons on item cards.',
        ],
      },
      {
        id: 'full-preview',
        title: 'Full Preview',
        section: 'editor',
        description:
          'Click the "open in new" icon in the top toolbar to open a full-screen preview of the entire form version. This shows all paragraphs rendered exactly as they will appear to patients. Use this to review the complete form before activating it.',
        mediaUrl: `${MEDIA_BASE}/15-full-preview.webm`,
        duration: 14,
        tips: [
          'The full preview shows all paragraphs in order.',
          'Check field labels, spacing, and layout before activating the version.',
        ],
        keyboardShortcut: 'Ctrl+P — Preview',
      },
      {
        id: 'saving-form',
        title: 'Saving Your Form',
        section: 'editor',
        description:
          'Click the "Save" button in the top toolbar or press Ctrl+S to save your changes. The version revision number auto-increments each time you save. A confirmation message appears when the save is successful. Make sure to save regularly to avoid losing your work.',
        mediaUrl: `${MEDIA_BASE}/16-saving-form.webm`,
        duration: 9,
        tips: [
          'Save frequently — especially after making significant changes.',
          'The revision number in the breadcrumb updates after each save.',
        ],
        keyboardShortcut: 'Ctrl+S — Save',
      },
      {
        id: 'print-to-pdf',
        title: 'Print to PDF',
        section: 'editor',
        description:
          'To export forms as PDF, go back to the Form Finder and click "Select Forms" to enter multi-select mode. Check the forms you want to include, then click "Print to PDF". The print preview dialog lets you review the output and save it as a PDF file.',
        mediaUrl: `${MEDIA_BASE}/17-print-to-pdf.webm`,
        duration: 20,
        tips: [
          'You can select multiple forms to combine into a single PDF.',
          'Use "Select All" and "Deselect All" for bulk operations.',
          'The PDF includes page breaks between forms.',
        ],
      },
    ],
  },
];
